import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  IconButton,
  Typography,
} from '@mui/material';
import {
  PersonSearch as PersonSearchIcon,
  PersonAdd as PersonAddIcon,
  Payment as PaymentIcon,
  LocationOn as LocationIcon,
  CameraAlt as CameraIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  AppLayout,
  ProgressBar,
  Button,
} from './UI';
import { DonorSearchStep } from './DonorSearchStep';
import { DonorCreationStep } from './DonorCreationStep';
import { DonationDetailsStep } from './DonationDetailsStep';
import { LocationForm } from './LocationForm';
import { PaymentPhotoStep } from './PaymentPhotoStep';
import { DonationSummaryStep } from './DonationSummaryStep';
import { Donor, CreateDonorRequest, CreateDonationRequest, PaymentMethod, GeolocationPosition } from '@/types';
import { apiService } from '@/services/api';
import { offlineStorageService } from '@/services/offlineStorage';
import { notificationService } from '@/services/notificationService';
import { appStateService } from '@/services/appStateService';

interface NewDonationWizardProps {
  onSuccess: () => void;
  onCancel: () => void;
  onNavigate?: (view: string) => void;
}

interface WizardData {
  selectedDonor?: Donor;
  newDonor?: CreateDonorRequest;
  donation: Partial<CreateDonationRequest>;
  paymentPhoto?: File;
  location?: GeolocationPosition;
}

const steps = [
  {
    id: 'donor-search',
    label: 'Ricerca Donatore',
    icon: <PersonSearchIcon />,
  },
  {
    id: 'donor-creation',
    label: 'Dati Donatore',
    icon: <PersonAddIcon />,
  },
  {
    id: 'donation-details',
    label: 'Dettagli Donazione',
    icon: <PaymentIcon />,
  },
  {
    id: 'location',
    label: 'Posizione',
    icon: <LocationIcon />,
  },
  {
    id: 'payment-photo',
    label: 'Foto Pagamento',
    icon: <CameraIcon />,
  },
  {
    id: 'summary',
    label: 'Riepilogo',
    icon: <CheckCircleIcon />,
  },
];


export const NewDonationWizard: React.FC<NewDonationWizardProps> = ({
  onSuccess,
  onCancel,
  onNavigate,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [wizardData, setWizardData] = useState<WizardData>({
    donation: {
      amount: 0,
      paymentMethod: 'CASH' as PaymentMethod,
      notes: '',
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isDataConfirmed, setIsDataConfirmed] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleNext = async () => {
    // Se siamo al passo 1 (dati donatore) e c'è un nuovo donatore (non selezionato dalla ricerca), controlla duplicati
    if (activeStep === 1 && wizardData.newDonor && !wizardData.selectedDonor) {
      console.log('🔍 Step 1: Checking duplicates for NEW donor (not selected from search)...');
      try {
        const duplicateCheck = await apiService.checkDonorDuplicates({
          email: wizardData.newDonor.email,
          fiscalCode: wizardData.newDonor.fiscalCode,
          vatNumber: wizardData.newDonor.vatNumber,
        });

        console.log('📋 Duplicate check result:', duplicateCheck);

        if (duplicateCheck.hasDuplicates) {
          console.log('❌ DUPLICATES FOUND - Blocking navigation');
          const { duplicates } = duplicateCheck;
          let errorMessage = 'Donatore già esistente:\n';
          
          if (duplicates.email) {
            errorMessage += `• Email: ${duplicates.email.firstName} ${duplicates.email.lastName} (${duplicates.email.email})\n`;
          }
          if (duplicates.fiscalCode) {
            errorMessage += `• Codice Fiscale: ${duplicates.fiscalCode.firstName} ${duplicates.fiscalCode.lastName} (${duplicates.fiscalCode.fiscalCode})\n`;
          }
          if (duplicates.vatNumber) {
            errorMessage += `• Partita IVA: ${duplicates.vatNumber.firstName} ${duplicates.vatNumber.lastName} (${duplicates.vatNumber.vatNumber})\n`;
          }
          
          setError(errorMessage.trim());
          notificationService.error('Duplicato trovato', 'Esiste già un donatore con questi dati');
          return; // Blocca la navigazione
        }
        
        console.log('✅ No duplicates found - Proceeding to next step');
      } catch (error) {
        console.error('❌ Error checking duplicates:', error);
        setError('Errore durante il controllo duplicati');
        notificationService.error('Errore', 'Errore durante il controllo duplicati');
        return; // Blocca la navigazione in caso di errore
      }
    } else if (activeStep === 1 && wizardData.selectedDonor) {
      console.log('✅ Step 1: Donor selected from search - No duplicate check needed');
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError(null);
    // Reset della checkbox quando si cambia step
    setIsDataConfirmed(false);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError(null);
    // Reset della checkbox quando si cambia step
    setIsDataConfirmed(false);
  };

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < activeStep) return { completed: true, current: false };
    if (stepIndex === activeStep) return { completed: false, current: true };
    return { completed: false, current: false };
  };

  const getCompletedSteps = () => {
    let completed = 0;
    
    // Step 0: Ricerca donatore - completato se c'è un donatore selezionato o nuovo
    if (wizardData.selectedDonor || wizardData.newDonor) completed++;
    
    // Step 1: Dati donatore - completato se c'è un donatore
    if (wizardData.selectedDonor || wizardData.newDonor) completed++;
    
    // Step 2: Dettagli donazione - completato se c'è un importo
    if (wizardData.donation.amount && wizardData.donation.amount > 0) completed++;
    
    // Step 3: Posizione - completato se c'è una posizione
    if (wizardData.location) completed++;
    
    // Step 4: Foto pagamento - sempre completato (opzionale)
    completed++;
    
    // Step 5: Riepilogo - non conta per i completati
    if (activeStep === 5) completed = 5;
    
    return completed;
  };

  const isCurrentStepValid = () => {
    const isValid = (() => {
      switch (activeStep) {
        case 0: // Ricerca donatore
          return !!(wizardData.selectedDonor || wizardData.newDonor);
        case 1: // Dati donatore
          if (wizardData.selectedDonor) {
            return true; // Donatore esistente, sempre valido
          }
          if (wizardData.newDonor) {
            const { fiscalCode, vatNumber, sdiCode, pec, firstName, lastName, email, gdprConsent } = wizardData.newDonor;
            
            // Validazione campi obbligatori
            if (!firstName || !lastName || !email || !gdprConsent) {
              return false;
            }
            
            // Validazione email
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
              return false;
            }
            
            // Validazione CF o P.IVA (almeno uno deve essere presente)
            const hasFiscalCode = fiscalCode && fiscalCode.trim() !== '';
            const hasVatNumber = vatNumber && vatNumber.trim() !== '';
            
            if (!hasFiscalCode && !hasVatNumber) {
              return false;
            }
            
            // Validazione formato CF (se presente)
            if (hasFiscalCode) {
              if (fiscalCode.length !== 16 || !/^[A-Z0-9]{16}$/.test(fiscalCode)) {
                return false;
              }
            }
            
            // Validazione formato P.IVA (se presente)
            if (hasVatNumber) {
              if (vatNumber.length !== 11 || !/^[0-9]{11}$/.test(vatNumber)) {
                return false;
              }
            }
            
            // Validazione formato SDI (se presente)
            if (sdiCode && sdiCode.trim() !== '') {
              if (sdiCode.length < 6 || sdiCode.length > 7 || !/^[A-Z0-9]{6,7}$/.test(sdiCode)) {
                return false;
              }
            }
            
            // Validazione formato PEC (se presente)
            if (pec && pec.trim() !== '') {
              if (!emailRegex.test(pec)) {
                return false;
              }
            }
            
            return true;
          }
          return false;
        case 2: // Dettagli donazione
          return !!(wizardData.donation.amount && wizardData.donation.amount > 0);
        case 3: // Posizione
          return !!wizardData.location;
        case 4: // Foto pagamento
          return true; // Opzionale
        case 5: // Riepilogo
          return isDataConfirmed; // Deve essere confermato
        default:
          return false;
      }
    })();
    
    return isValid;
  };

  const handleStepData = (stepData: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...stepData }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Validazione posizione obbligatoria
      if (!wizardData.location) {
        throw new Error('La posizione è obbligatoria per completare la donazione');
      }

      let donorId: string;

      // Crea o ottieni ID donatore
      if (wizardData.selectedDonor) {
        donorId = wizardData.selectedDonor.id;
      } else if (wizardData.newDonor) {
        if (isOnline) {
          // Pulisci i dati del donatore prima di inviarli
          const cleanDonorData = {
            ...wizardData.newDonor,
            company: wizardData.newDonor.company?.trim() ? wizardData.newDonor.company.trim() : undefined,
            phone: wizardData.newDonor.phone?.trim() ? wizardData.newDonor.phone.trim() : undefined,
            address: wizardData.newDonor.address?.trim() ? wizardData.newDonor.address.trim() : undefined,
            city: wizardData.newDonor.city?.trim() ? wizardData.newDonor.city.trim() : undefined,
            postalCode: wizardData.newDonor.postalCode?.trim() ? wizardData.newDonor.postalCode.trim() : undefined,
            country: wizardData.newDonor.country?.trim() ? wizardData.newDonor.country.trim() : undefined,
            fiscalCode: wizardData.newDonor.fiscalCode?.trim() ? wizardData.newDonor.fiscalCode.trim() : undefined,
            vatNumber: wizardData.newDonor.vatNumber?.trim() ? wizardData.newDonor.vatNumber.trim() : undefined,
            sdiCode: wizardData.newDonor.sdiCode?.trim() ? wizardData.newDonor.sdiCode.trim() : undefined,
          };
          
          const createdDonor = await apiService.createDonor(cleanDonorData);
          donorId = createdDonor.id;
        } else {
          // Salva donatore offline
          const tempId = `donor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const cleanDonorData = {
            ...wizardData.newDonor,
            company: wizardData.newDonor.company?.trim() ? wizardData.newDonor.company.trim() : undefined,
            phone: wizardData.newDonor.phone?.trim() ? wizardData.newDonor.phone.trim() : undefined,
            address: wizardData.newDonor.address?.trim() ? wizardData.newDonor.address.trim() : undefined,
            city: wizardData.newDonor.city?.trim() ? wizardData.newDonor.city.trim() : undefined,
            postalCode: wizardData.newDonor.postalCode?.trim() ? wizardData.newDonor.postalCode.trim() : undefined,
            country: wizardData.newDonor.country?.trim() ? wizardData.newDonor.country.trim() : undefined,
            fiscalCode: wizardData.newDonor.fiscalCode?.trim() ? wizardData.newDonor.fiscalCode.trim() : undefined,
            vatNumber: wizardData.newDonor.vatNumber?.trim() ? wizardData.newDonor.vatNumber.trim() : undefined,
            sdiCode: wizardData.newDonor.sdiCode?.trim() ? wizardData.newDonor.sdiCode.trim() : undefined,
          };
          
          await offlineStorageService.saveOfflineDonor({
            ...cleanDonorData,
            id: tempId,
          });
          donorId = tempId;
        }
      } else {
        throw new Error('Nessun donatore selezionato');
      }

      // Ottieni ID del collector (utente corrente)
      const currentUser = appStateService.getState().user;
      if (!currentUser) {
        throw new Error('Utente non autenticato');
      }

      // Crea donazione
      const donationData: CreateDonationRequest = {
        ...wizardData.donation,
        donorId,
        collectorId: currentUser.id,
        latitude: wizardData.location?.latitude || 0,
        longitude: wizardData.location?.longitude || 0,
        address: wizardData.location?.address || 'Posizione non disponibile',
      } as CreateDonationRequest;

      if (isOnline) {
        // Crea donazione online
        const createdDonation = await apiService.createDonation(donationData);
        
        // Carica foto pagamento se presente
        if (wizardData.paymentPhoto) {
          try {
            // Carica la foto del documento
            const photoUploadResult = await apiService.uploadDocumentPhoto({
              file: wizardData.paymentPhoto,
              donationId: createdDonation.id,
            });

            // Aggiorna la donazione con i metadati della foto
            await apiService.updateDonation(createdDonation.id, {
              documentPhotoS3Key: photoUploadResult.s3Key,
              documentPhotoS3Url: photoUploadResult.s3Url,
              documentPhotoOriginalName: photoUploadResult.originalName,
              documentPhotoMimeType: photoUploadResult.mimeType,
              documentPhotoFileSize: photoUploadResult.fileSize,
            });
          } catch (uploadError) {
            console.warn('Errore durante l\'upload della foto pagamento:', uploadError);
            // Non bloccare il processo se l'upload della foto fallisce
            notificationService.warning(
              'Foto Pagamento',
              'La donazione è stata creata ma la foto non è stata caricata. Puoi riprovare più tardi.'
            );
          }
        }
      } else {
        // Salva donazione offline
        const localId = await offlineStorageService.saveOfflineDonation(donationData);

        // Salva foto pagamento offline se presente
        if (wizardData.paymentPhoto) {
          await offlineStorageService.saveOfflineReceipt(localId, wizardData.paymentPhoto);
        }
      }

      // Notifica successo
      notificationService.success(
        'Donazione Creata',
        `Donazione di €${donationData.amount} creata con successo!`
      );

      onSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Errore durante la creazione della donazione';
      setError(errorMessage);
      notificationService.error('Errore', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <DonorSearchStep
            onDonorSelected={(donor) => {
              handleStepData({ selectedDonor: donor, newDonor: undefined });
              handleNext();
            }}
            onDonorDeselected={() => {
              handleStepData({ selectedDonor: undefined, newDonor: undefined });
            }}
            onNewDonor={() => {
              handleStepData({ selectedDonor: undefined, newDonor: undefined });
              handleNext();
            }}
            selectedDonor={wizardData.selectedDonor}
            showNavigationButtons={false}
            onCancel={onCancel}
          />
        );
      case 1:
        return (
          <DonorCreationStep
            initialData={wizardData.newDonor}
            selectedDonor={wizardData.selectedDonor}
            onDataChange={(donorData) => handleStepData({ newDonor: donorData })}
            onNext={handleNext}
            onBack={handleBack}
            showNavigationButtons={false}
          />
        );
      case 2:
        return (
          <DonationDetailsStep
            initialData={wizardData.donation}
            onDataChange={(donationData) => handleStepData({ donation: donationData })}
            onNext={handleNext}
            onBack={handleBack}
            showNavigationButtons={false}
          />
        );
      case 3:
        return (
          <LocationForm
            location={wizardData.location}
            onChange={(location) => handleStepData({ location })}
            onSubmit={handleNext}
            onBack={handleBack}
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
            showNavigationButtons={false}
          />
        );
      case 4:
        return (
          <PaymentPhotoStep
            initialPhoto={wizardData.paymentPhoto}
            onPhotoChange={(photo) => handleStepData({ paymentPhoto: photo })}
            onNext={handleNext}
            onBack={handleBack}
            showNavigationButtons={false}
          />
        );
      case 5:
        return (
          <DonationSummaryStep
            data={wizardData}
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
            isOnline={isOnline}
            showNavigationButtons={false}
            isDataConfirmed={isDataConfirmed}
            onDataConfirmedChange={setIsDataConfirmed}
            onCancel={onCancel}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AppLayout
      title="Nuova Donazione"
      showSyncButton={true}
      currentView="new-donation"
      onNavigate={onNavigate}
    >
      {/* LAYOUT A BLOCCHI - SOLO CENTRALE SCROLLABILE */}
      <Box sx={{ 
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        background: 'background.default', // Usa il gradiente del tema
      }}>
        
        {/* BLOCCO SUPERIORE - ProgressBar FISSO */}
        <Box sx={{ 
          position: 'fixed',
          top: 60,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          borderBottom: 2,
          borderColor: 'primary.main',
          p: 1.5, // Ridotto da p: 3 a p: 1.5
          boxShadow: 1,
        }}>
          <ProgressBar
            steps={steps.map((step, index) => ({
              ...step,
              ...getStepStatus(index),
            }))}
            currentStep={activeStep}
            totalSteps={steps.length}
            completedSteps={getCompletedSteps()}
          />
        </Box>

        {/* BLOCCO CENTRALE - Contenuto Scrollabile */}
        <Box sx={{ 
          flex: 1,
          overflow: 'auto',
          backgroundColor: 'background.default', // Usa il gradiente del tema
          p: 2, // Ridotto da p: 3 a p: 2
          mt: '70px', // Spazio per ProgressBar fissa (ridotto ulteriormente)
          mb: '80px', // Spazio per pulsanti fissi (ridotto da 100px)
          minHeight: 0,
        }}>
          {renderStepContent(activeStep)}
        </Box>

        {/* BLOCCO INFERIORE - Pulsanti FISSI */}
        <Box sx={{ 
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          borderTop: 2,
          borderColor: 'primary.main',
          p: 2, // Ridotto da p: 3 a p: 2
          boxShadow: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}>
          {/* Pulsante Indietro */}
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleBack}
            disabled={isSubmitting || activeStep === 0}
            startIcon={<ArrowBackIcon />}
            sx={{ 
              minWidth: 140,
              height: 48,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Indietro
          </Button>

          {/* Spazio centrale */}
          <Box sx={{ flex: 1 }} />

          {/* Pulsante Avanti/Completa */}
          <Button
            variant="contained"
            color="primary"
            onClick={activeStep >= steps.length - 1 ? handleSubmit : handleNext}
            disabled={isSubmitting || !isCurrentStepValid()}
            startIcon={<ArrowForwardIcon />}
            sx={{ 
              minWidth: 140,
              height: 48,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            {activeStep >= steps.length - 1 ? 'Completa' : 'Avanti'}
          </Button>
        </Box>

        {/* Error display - Fuori dai blocchi principali */}
        {error && (
          <Box sx={{ 
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            maxWidth: 400,
            width: '90%',
          }}>
            <Alert 
              severity="error" 
              sx={{ 
                fontSize: '1rem',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'error.light',
                }
              }}
              onClick={() => setError(null)}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setError(null);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              <Typography 
                component="pre" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  lineHeight: 1.4
                }}
              >
                {error}
              </Typography>
            </Alert>
          </Box>
        )}
      </Box>
    </AppLayout>
  );
};
